import type { JSX } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  ChevronRightIcon,
  DocumentDuplicateIcon,
  ExclamationTriangleIcon,
  LinkSlashIcon,
} from '@heroicons/react/24/outline';
import { Button, Tooltip } from '@heroui/react';
import clsx from 'clsx';

import type { JiraGroup, JiraSection } from '~/hooks/useJiraFiltering';
import { useJiraGrouping } from '~/hooks/useJiraFiltering';
import type { JiraIssue } from '~/services/api/jira';

import { CloneToProjectButton } from './CloneToProjectButton';
import { isJiraReconnectRequired } from './jiraErrors';
import { JiraIcon } from './JiraIcon';
import { getIssueTypeColor } from './utils';

// Section header dot colors
const SECTION_DOT_COLOR: Record<JiraSection, string> = {
  new: 'bg-primary-400',
  cloned: 'bg-secondary-400',
  done: 'bg-success-400',
};

interface IssueRowProps {
  issue: JiraIssue;
  isSelected: boolean;
  onSelect: () => void;
}

interface IssueSectionProps {
  collapsedSections: Set<JiraSection>;
  group: JiraGroup;
  onSelectIssue: (issueKey: string) => void;
  onToggle: (section: JiraSection) => void;
  selectedIssueKey: string | null;
}

interface JiraSidebarProps {
  error: unknown;
  hasReconnectError: boolean;
  isConnected: boolean;
  isLoading: boolean;
  issues: JiraIssue[];
  onReconnect: () => void;
  onRetry: () => void;
  onSelectIssue: (issueKey: string) => void;
  selectedIssueKey: string | null;
  siteUrl: string | null;
}

function IssueRow({ isSelected, issue, onSelect }: IssueRowProps): JSX.Element {
  const handleKeyDown = (event: React.KeyboardEvent): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onSelect();
    }
  };

  return (
    <div
      role="option"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={handleKeyDown}
      className={clsx(
        'group px-3 py-2.5 rounded-lg transition-colors cursor-pointer',
        'bg-white/[0.08] focus:outline-none focus:ring-2 focus:ring-primary',
        isSelected ? 'bg-white/[0.15] ring-1 ring-white/[0.12]' : 'hover:bg-white/[0.12]',
      )}
      aria-selected={isSelected}
    >
      {/* First row: [Jira ID] [Title] ... [actions on hover] */}
      <div className="flex items-center gap-2 mb-0.5">
        <span className="text-primary font-mono text-xs font-semibold shrink-0 tabular-nums">
          {issue.key}
        </span>
        <span className="text-white/90 text-sm line-clamp-1 flex-1">{issue.summary}</span>
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
        <div
          onClick={e => e.stopPropagation()}
          className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity duration-100"
        >
          <Tooltip content="Open in Jira" placement="top">
            <a
              href={issue.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center p-1 rounded text-white/40 hover:text-white/80 hover:bg-white/10 transition-colors"
              aria-label="Open in Jira"
            >
              <JiraIcon className="w-3.5 h-3.5" />
            </a>
          </Tooltip>
          {issue.linked_project_id ? (
            <Tooltip content="Go to project" placement="top">
              <Link
                to={`/projects/${issue.linked_project_id}`}
                className="inline-flex items-center justify-center p-1 rounded text-primary hover:bg-primary/10 transition-colors"
                aria-label="Go to linked project"
              >
                <DocumentDuplicateIcon className="w-3.5 h-3.5" />
              </Link>
            </Tooltip>
          ) : (
            <CloneToProjectButton issue={issue} iconOnly />
          )}
        </div>
      </div>
      {/* Second row: metadata with middle dot separator */}
      <div className="flex items-center gap-1.5 text-xs">
        <span
          className={`px-1.5 py-0.5 font-medium rounded ${getIssueTypeColor(issue.issue_type)}`}
        >
          {issue.issue_type}
        </span>
        <span className="text-white/25">&middot;</span>
        <span className={`text-white/60`}>{issue.status}</span>
      </div>
    </div>
  );
}

function IssueSection({
  collapsedSections,
  group,
  onSelectIssue,
  onToggle,
  selectedIssueKey,
}: IssueSectionProps): JSX.Element {
  const isCollapsed = collapsedSections.has(group.key);
  const isEmpty = group.issues.length === 0;

  return (
    <div className={clsx('mb-4', isEmpty && 'opacity-40')}>
      {/* Section header — no divider line, spacing-only hierarchy */}
      <button
        type="button"
        onClick={() => onToggle(group.key)}
        className="flex items-center justify-between w-full py-2 text-sm font-semibold text-white/50 hover:text-white/80 transition-colors"
        aria-expanded={!isCollapsed}
        aria-controls={`jira-section-${group.key}`}
      >
        <div className="flex items-center gap-2">
          <ChevronRightIcon
            className={clsx('h-3.5 w-3.5 shrink-0 transition-transform duration-150 ease-out', {
              'rotate-90': !isCollapsed,
            })}
          />
          <span className={clsx('w-2 h-2 rounded-full shrink-0', SECTION_DOT_COLOR[group.key])} />
          <span>{group.label}</span>
        </div>
        <span className="text-xs text-white/30">{group.issues.length}</span>
      </button>

      {/* Collapsible issue list */}
      <div
        id={`jira-section-${group.key}`}
        className={clsx('overflow-hidden transition-all duration-150 ease-out', {
          'max-h-0': isCollapsed,
          'max-h-[5000px]': !isCollapsed,
        })}
      >
        <div className="flex flex-col gap-1.5 pt-1">
          {group.issues.map(issue => (
            <IssueRow
              key={issue.key}
              issue={issue}
              isSelected={issue.key === selectedIssueKey}
              onSelect={() => onSelectIssue(issue.key)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export function JiraSidebar({
  error,
  hasReconnectError,
  isConnected,
  isLoading,
  issues,
  onReconnect,
  onRetry,
  onSelectIssue,
  selectedIssueKey,
  siteUrl,
}: JiraSidebarProps): JSX.Element {
  const { collapsedSections, groups, toggleSection } = useJiraGrouping(issues);

  const hasIssues = isConnected && !isLoading && !error && issues.length > 0;

  return (
    <aside className="w-[420px] flex-shrink-0 flex flex-col bg-background border-r border-default h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-6 pt-6 pb-4 shrink-0">
        <h3 className="text-xl font-semibold text-foreground">Jira</h3>
        {siteUrl && (
          <a href={siteUrl} target="_blank" rel="noopener noreferrer" aria-label="Open Jira">
            <Button
              color="primary"
              endContent={<ArrowTopRightOnSquareIcon className="w-4 h-4" />}
              tabIndex={-1}
            >
              Open Jira
            </Button>
          </a>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-6">
        {/* Not connected state */}
        {!isConnected && !hasReconnectError && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <JiraIcon className="w-12 h-12 text-muted mb-4 opacity-50" />
            <p className="text-foreground font-medium mb-2">Not Connected</p>
            <p className="text-muted text-sm mb-4">Connect Jira to see your tickets</p>
            <Button color="primary" size="sm" onPress={onReconnect}>
              Go to Profile
            </Button>
          </div>
        )}

        {/* Loading state */}
        {isConnected && isLoading && (
          <div
            role="status"
            aria-label="Loading Jira tickets"
            aria-busy="true"
            className="flex flex-col items-center justify-center py-12 text-center"
          >
            <JiraIcon className="w-12 h-12 text-muted mb-4 opacity-50" />
            <p className="text-muted text-sm">Loading tickets...</p>
          </div>
        )}

        {/* Reconnect required state */}
        {hasReconnectError && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <LinkSlashIcon className="w-12 h-12 text-warning mb-4" />
            <p className="text-foreground font-medium mb-2">Session Expired</p>
            <p className="text-muted text-sm mb-4">Please reconnect to continue</p>
            <Button color="primary" size="sm" onPress={onReconnect}>
              Go to Profile
            </Button>
          </div>
        )}

        {/* Generic error state */}
        {!!error && !isJiraReconnectRequired(error) && !isLoading && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <ExclamationTriangleIcon className="w-12 h-12 text-destructive mb-4" />
            <p className="text-foreground font-medium mb-2">Failed to load</p>
            <p className="text-muted text-sm mb-4">
              {error instanceof Error ? error.message : 'An error occurred'}
            </p>
            <Button
              color="primary"
              size="sm"
              onPress={onRetry}
              startContent={<ArrowPathIcon className="w-4 h-4" />}
            >
              Retry
            </Button>
          </div>
        )}

        {/* Empty state */}
        {isConnected && !isLoading && !error && issues.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <JiraIcon className="w-12 h-12 text-muted mb-4 opacity-50" />
            <p className="text-foreground font-medium mb-2">No tickets</p>
            <p className="text-muted text-sm">No Jira tickets assigned to you</p>
          </div>
        )}

        {/* Grouped issue list */}
        {hasIssues && (
          <div role="listbox" aria-label="Jira tickets">
            {groups.map(group => (
              <IssueSection
                key={group.key}
                group={group}
                collapsedSections={collapsedSections}
                onToggle={toggleSection}
                onSelectIssue={onSelectIssue}
                selectedIssueKey={selectedIssueKey}
              />
            ))}
          </div>
        )}
      </div>
    </aside>
  );
}
